import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import DialogProductGroup from '@/components/DialogProductGroup/DialogProductGroup';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import SimpleButton from '@/components/UI/SimpleButton/SimpleButton';
import Spinner from '@/components/UI/Spinner/Spinner';
import TableListEditableNew from '@/components/UI/TableListEditable/TableListEditableNew';
import { validateProductGroup, validateProductGroupMGC } from '@/helpers/administrationUtils';
import { CUSTOM_COLUMNS_TYPES, ERROR, propsForNonTypeScriptComponent } from '@/helpers/constants';
import { toCamelCase } from '@/helpers/utils';
import useAppLanguage from '@/hooks/useAppLanguage';
import useGlobalSpinnerContext from '@/hooks/useGlobalSpinner';
import useToastContext from '@/hooks/useToastContext';
import { setPlantsForm } from '@/redux/Forms/forms.action';
import { getPlantsForm } from '@/redux/Forms/forms.reducer';
import administrationServices from '@/services/administrationServices';
import titleService from '@/services/titleService';
import type { ColumnConfig, MultiSelectionColumnConfig, Plant, UsersSelectionColumnConfig } from '@/types';
import { CustomColumnsType, ProductGroupName, ProductGroupT, TableProps } from '@/types/ProductGroup';
import styles from './ProductGroup.module.scss';

export const PCROSS = 'PCROSS';
export const PMGC = 'PMGC';
export const PG = 'PG';

// Column names
const COLUMN_NAME = 'name';
const COLUMN_PLANTS = 'plants';
const COLUMN_COORDINATORS = 'coordinators';
const COLUMN_MAILBOXES = 'mailboxes';

const getAllPlantsData = (selected, allPlants) => allPlants.filter((plant) => selected?.includes(plant.wkzNumber));
const transformMailboxJoin = (pGMGC) => pGMGC.map((pg) => ({ ...pg, mailboxes: pg.mailboxes ? pg.mailboxes.join(',') : pg.mailboxes }));
const transformMailboxSplit = (pGMGC) => ({ ...pGMGC, mailboxes: pGMGC.mailboxes ? pGMGC.mailboxes.split(',') : pGMGC.mailboxes });

interface ProductGroupProps {
  plants: Plant[];
  setPlants: (plants: Plant[]) => void;
}

const ProductGroup: React.FC<ProductGroupProps> = (props) => {
  const { plants, setPlants } = props;
  const { t } = useTranslation();
  const lang = useAppLanguage();
  const [isCreatePGOpen, setCreatePGOpen] = useState(false);
  const [productGroups, setProductGroups] = useState<ProductGroupT[]>([]);
  const [productGroupsMGC, setProductGroupsMGC] = useState<ProductGroupT[]>([]);
  const [crossProductGroup, setCrossProductGroup] = useState<ProductGroupT[]>([]);
  const [loading, setLoading] = useState(true);
  const { addToast } = useToastContext();
  const { setLoading: setGlobalLoading } = useGlobalSpinnerContext();

  useEffect(() => {
    titleService.updatePageTitle(t('dashboard.administration'), t('administration.productGroup.productGroup'));
  }, [t, lang]);

  const initTables = useCallback(() => {
    setLoading(true);
    Promise.all([
      administrationServices.getPlants(),
      administrationServices.getProductGroups(),
      administrationServices.getProductGroupsMGC(),
      administrationServices.getProductGroupsCross(),
    ])
      .then((res) => {
        const [resPlants, resProductGroups, resProductGroupsMGC, resCrossProduct] = res;
        setPlants(resPlants.plants);
        setProductGroups(resProductGroups.productGroups);
        setProductGroupsMGC(transformMailboxJoin(resProductGroupsMGC.productGroups));
        setCrossProductGroup(resCrossProduct.productGroups);
      })
      .catch((error) => addToast(error.message, 'ERROR'))
      .finally(() => {
        setLoading(false);
        setCreatePGOpen(false);
      });
  }, [addToast, setPlants]);

  useEffect(() => {
    initTables();
  }, [initTables]);

  const checkEditedColumn = useCallback((newValue: string[], tableProps: TableProps, type: CustomColumnsType, list: ProductGroupT[]) => {
    const { column, row } = tableProps;
    const newProductGroups = [CUSTOM_COLUMNS_TYPES.USERS_SELECTION, CUSTOM_COLUMNS_TYPES.MULTI_SELECTION].includes(type)
      ? list.map((pg) => (pg.id === row.id ? { ...row, [column?.name]: newValue } : pg))
      : newValue;
    const editedGroup = (newProductGroups as ProductGroupT[]).find((pg) => pg.id === row.id);

    return { editedGroup, newProductGroups };
  }, []);

  const editProductGroup = useCallback((newValue: string[], tableProps: TableProps, type: CustomColumnsType) => {
    const { editedGroup, newProductGroups } = checkEditedColumn(newValue, tableProps, type, productGroups);

    if (editedGroup && validateProductGroup(editedGroup, addToast, t)) {
      setGlobalLoading(true);
      administrationServices.editProductGroup(editedGroup.id, editedGroup, false)
        .then(() => {
          setProductGroups(newProductGroups as ProductGroupT[]);
        })
        .catch((error) => {
          addToast(t(`administration.productGroup.${toCamelCase(error.code)}`), ERROR);
        })
        .finally(() => setGlobalLoading(false));
    }
  }, [addToast, checkEditedColumn, productGroups, setGlobalLoading, t]);

  const editProductGroupMGC = useCallback((newValue: string[], tableProps: TableProps, type: CustomColumnsType) => {
    const { editedGroup, newProductGroups } = checkEditedColumn(newValue, tableProps, type, productGroupsMGC);

    if (!editedGroup || !validateProductGroupMGC(editedGroup, addToast, t)) {
      return;
    }

    setGlobalLoading(true);
    const backPGSave = transformMailboxSplit(editedGroup);
    administrationServices.editProductGroup(backPGSave.id, backPGSave, true)
      .then(() => {
        setProductGroupsMGC(newProductGroups as ProductGroupT[]);
        initTables();
      })
      .catch((error) => {
        addToast(t(`administration.productGroup.${toCamelCase(error.code)}`), ERROR);
      })
      .finally(() => setGlobalLoading(false));
  }, [addToast, checkEditedColumn, initTables, productGroupsMGC, setGlobalLoading, t]);

  const editProductCross = useCallback((newValue: string[], tableProps: TableProps, type: CustomColumnsType) => {
    const { editedGroup, newProductGroups } = checkEditedColumn(newValue, tableProps, type, crossProductGroup);
    setGlobalLoading(true);
    administrationServices.editProductGroupCross(editedGroup && editedGroup.id, editedGroup)
      .then(() => {
        setCrossProductGroup(newProductGroups as ProductGroupT[]);
      })
      .catch((error) => {
        addToast(t(`administration.productGroup.${toCamelCase(error.code)}`), ERROR);
      })
      .finally(() => setGlobalLoading(false));
  }, [addToast, checkEditedColumn, crossProductGroup, setGlobalLoading, t]);

  const buildPlantsMultiSelection = useCallback((
    title: string,
    editHandler: (newValue: string[], tableProps: TableProps, type: CustomColumnsType) => void,
  ): MultiSelectionColumnConfig => ({
    options: plants,
    getAllSelectedData: (selected: unknown[]) => getAllPlantsData(selected as string[], plants),
    getOptionValue: (option) => (option as Plant).wkzNumber,
    multiSelectionProps: { maxChip: 100, isPlants: true, size: 'medium' },
    onChange: ({ rowData, columnName, value }) => {
      const tableProps: TableProps = {
        isFormatter: false,
        column: { name: columnName, title },
        row: rowData as ProductGroupT,
        value: value as string[],
      };

      editHandler(value as string[], tableProps, CUSTOM_COLUMNS_TYPES.MULTI_SELECTION);
    },
  }), [plants]);

  const buildMultiUsersSelection = useCallback((
    title: string,
    editHandler: (newValue: string[], tableProps: TableProps, type: CustomColumnsType) => void,
  ): UsersSelectionColumnConfig => ({
    popupTitle: t('administration.productGroup.userIDCC'),
    onChange: ({ rowData, columnName, value }) => {
      const tableProps: TableProps = {
        isFormatter: false,
        column: { name: columnName, title: title },
        row: rowData as ProductGroupT,
        value: Array.isArray(value) ? value : [],
      };

      editHandler(Array.isArray(value) ? value : [], tableProps, CUSTOM_COLUMNS_TYPES.USERS_SELECTION);
    },
  }), [t]);

  const plantPredicate = useCallback((value: unknown) => {
    const plantsData = getAllPlantsData(value as string[], plants);

    return plantsData.map((plant) => plant.plantName).join(', ');
  }, [plants]);

  // First Table

  const columnsPG = useMemo<ColumnConfig[]>(() => [
    { name: COLUMN_NAME, title: t('administration.productGroup.productGroup') },
    {
      name: COLUMN_PLANTS,
      title: t('administration.productGroup.dedicatedPlants'),
      filterValueGetter: ({ value }) => plantPredicate(value),
      multiSelection: buildPlantsMultiSelection(t('administration.productGroup.dedicatedPlants'), editProductGroup),
      wrapText: true,
    },
    {
      name: COLUMN_COORDINATORS, title: t('administration.productGroup.userIDCC'),
      wrapText: true,
      usersSelection: buildMultiUsersSelection(t('administration.productGroup.userIDCC'), editProductGroup),
    },
  ], [t, buildPlantsMultiSelection, editProductGroup, buildMultiUsersSelection, plantPredicate]);

  // Second Table

  const columnsPGMGC = useMemo<ColumnConfig[]>(() => ([
    {
      name: COLUMN_COORDINATORS,
      title: t('administration.productGroup.userIDCC'),
      usersSelection: buildMultiUsersSelection(t('administration.productGroup.userIDCC'), editProductGroupMGC),
    },
    {
      name: COLUMN_MAILBOXES,
      title: t('administration.productGroup.mBox'),
      inputText: {
        onChange: ({ rowData, value }) => {
          rowData[COLUMN_MAILBOXES] = value;
        },
        onBlur: ({ rowData, value }) => {
          const updatedGroup = { ...(rowData as ProductGroupT), [COLUMN_MAILBOXES]: value };
          const updatedList = productGroupsMGC.map((pg) => (pg.id === (rowData as ProductGroupT).id ? updatedGroup as unknown as ProductGroupT : pg));

          if (!validateProductGroupMGC(updatedGroup, addToast, t)) {
            return;
          }

          setGlobalLoading(true);
          const backPGSave = transformMailboxSplit(updatedGroup);
          administrationServices.editProductGroup(backPGSave.id, backPGSave, true)
            .then(() => {
              setProductGroupsMGC(updatedList);
              initTables();
            })
            .catch((error) => {
              addToast(t(`administration.productGroup.${toCamelCase(error.code)}`), ERROR);
            })
            .finally(() => setGlobalLoading(false));
        },
      },
    },
  ]), [t, editProductGroupMGC, productGroupsMGC, addToast, setGlobalLoading, initTables, buildMultiUsersSelection]);

  // Third Table

  const columnsCross = useMemo<ColumnConfig[]>(() => [
    {
      name: COLUMN_PLANTS,
      title: t('administration.productGroup.crossPlants'),
      wrapText: true,
      filterValueGetter: ({ value }) => plantPredicate(value),
      multiSelection: buildPlantsMultiSelection(t('administration.productGroup.crossPlants'), editProductCross),
    },
  ], [t, buildPlantsMultiSelection, editProductCross, plantPredicate]);

  const handlePGsTextEdit = ({ changed }, product: ProductGroupT[], productName: ProductGroupName) => {
    const changedData = changed[Object.keys(changed)[0]];

    if (changedData !== undefined) {
      const changedRows = product.map((row, index) => (changed[index] ? { ...row, ...changed[index] } : row));
      const rowEdited = changedRows[Object.keys(changed)[0]];

      switch (productName) {
        case PG:
          editProductGroup(changedRows, rowEdited, CUSTOM_COLUMNS_TYPES.TEXT);
          break;
        case PMGC:
          editProductGroupMGC(changedRows, rowEdited, CUSTOM_COLUMNS_TYPES.TEXT);
          break;
        case PCROSS:
          editProductCross(changedRows, rowEdited, CUSTOM_COLUMNS_TYPES.TEXT);
          break;
        default:
          return;
      }
    };
  };

  return (
    <>
      <div className={styles.Plants}>
        <AccordionPanel title={`${t('administration.productGroup.CCProductGroups')} ${`(${productGroups.length})`}`}>
          {loading && (<Spinner isVisible isMask />)}
          {!loading && (
            <>
              <div className={styles.ButtonsTable}>
                <div className={styles.ButtonsContentTableRight}>
                  <SimpleButton
                    id="btn-add-product-group"
                    color="primary"
                    buttonText={t('administration.productGroup.addPG')}
                    handleClick={() => setCreatePGOpen(true)}
                  />
                </div>
              </div>
              <div className="w-100">
                <TableListEditableNew
                  isFiltering
                  list={productGroups}
                  commitChanges={(prop) => handlePGsTextEdit(prop, productGroups, PG)}
                  columns={columnsPG}
                  {...propsForNonTypeScriptComponent}
                />
              </div>
            </>
          )}
        </AccordionPanel>
        <AccordionPanel title={t('administration.productGroup.CCMGC')}>
          {loading && (<Spinner isVisible isMask />)}
          {!loading && (
            <>
              <div className="w-100">
                <TableListEditableNew
                  list={productGroupsMGC}
                  columns={columnsPGMGC}
                  isFiltering
                  columnId="id"
                  commitChanges={(tableProps) => handlePGsTextEdit(tableProps, productGroupsMGC, PMGC)}
                  {...propsForNonTypeScriptComponent}
                />
              </div>
            </>
          )}
        </AccordionPanel>
        <AccordionPanel title={t('administration.productGroup.crossPlants')}>
          {loading && (<Spinner isVisible isMask />)}
          {!loading && (
            <div className="w-100">
              <TableListEditableNew
                list={crossProductGroup}
                columns={columnsCross}
                isPaginated={false}
              />
            </div>
          )}
        </AccordionPanel>
      </div>
      {isCreatePGOpen
        && (
          <DialogProductGroup
            title={t('administration.productGroup.addPG')}
            close={() => setCreatePGOpen(false)}
            reloadProductGroups={initTables}
          />
        )}
    </>
  );
};

const mapStateToProps = (state) => ({
  plants: getPlantsForm(state),
});

const actionCreators = {
  setPlants: setPlantsForm,
};

export default connect(mapStateToProps, actionCreators)(ProductGroup);
